// ANANT CITYPRO — All Shops Showcase (public grid + classified banners)
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

export default function CityProShowcase() {
  const [shops, setShops] = useState([]);
  const [banners, setBanners] = useState([]);
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get('/citypro/showcase', { params: { city: city || undefined, category: category || undefined, limit: 200 } }),
        api.get('/citypro/config'),
      ]);
      setShops(s.data?.shops || []);
      setBanners(s.data?.banners || []);
      setCats(c.data?.merchant_categories || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [city, category]);

  const tierColor = { enterprise: 'from-amber-400 to-yellow-500', premium: 'from-fuchsia-500 to-pink-500', basic: 'from-emerald-500 to-cyan-500' };
  const tierLabel = { enterprise: '💎 ENTERPRISE', premium: '⭐ PREMIUM', basic: '' };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-fuchsia-950 via-slate-950 to-cyan-950 text-white" data-testid="citypro-showcase">
      <div className="max-w-6xl mx-auto px-3 pb-16">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/citypro" className="text-xs text-slate-400 hover:text-white">← Back to CityPro</Link>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">All Shops Showcase</h1>
        <p className="text-[11px] opacity-70 mb-3">Discover local shops by plan tier · classified banners above · WhatsApp direct link</p>

        {/* Classified High-Priority Banners */}
        {banners.length > 0 && (
          <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-r from-amber-950/40 to-fuchsia-950/40 p-3 mb-3" data-testid="cp-sc-banners">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">📣 Classified Sponsored Ads</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {banners.map((b) => (
                <a key={b.id} href={b.link_url || '#'} target="_blank" rel="noreferrer"
                   data-testid={`cp-sc-banner-${b.id}`}
                   className="shrink-0 w-64 sm:w-80 rounded-xl overflow-hidden border border-amber-400/40 hover:border-amber-400 transition">
                  <div className="aspect-[3/1] bg-slate-900 bg-cover bg-center" style={{ backgroundImage: `url(${b.image_url})` }} />
                  <div className="p-2 text-xs font-black bg-slate-900/80">{b.title}</div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          <input placeholder="🌆 City filter" value={city} onChange={(e) => setCity(e.target.value)} data-testid="cp-sc-city"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cp-sc-cat"
                  className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            <option value="">— All categories —</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>
        </div>

        {loading && <div className="text-center opacity-70 py-8">⏳ Loading shops…</div>}

        {/* Shop Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {shops.map((s) => {
            const wa = s.whatsapp_number || s.phone;
            const waUrl = wa ? `https://wa.me/${String(wa).replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Hi ${s.shop_name || s.username}, saw your shop on Anant CityPro 🙏`)}` : null;
            return (
              <div key={s.user_id} className="rounded-2xl overflow-hidden border-2 border-slate-700 bg-slate-900/70 hover:border-fuchsia-400 transition group" data-testid={`cp-sc-shop-${s.user_id}`}>
                <div className="aspect-[4/3] bg-slate-800 bg-cover bg-center relative"
                     style={{ backgroundImage: s.front_image_url ? `url(${s.front_image_url})` : (s.banner_url ? `url(${s.banner_url})` : undefined) }}>
                  {s.plan_tier && s.plan_tier !== 'basic' && (
                    <div className={`absolute top-1 left-1 px-2 py-0.5 rounded-full text-[9px] font-black bg-gradient-to-r ${tierColor[s.plan_tier] || tierColor.basic} text-black`}>
                      {tierLabel[s.plan_tier]}
                    </div>
                  )}
                  {s.special_offer && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-1.5">
                      <div className="text-[10px] font-black text-amber-200 truncate">🎁 {s.special_offer}</div>
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-xs font-black truncate">{s.shop_name || `@${s.username}`}</div>
                  <div className="text-[10px] opacity-70 truncate">{s.city || 'City n/a'} · {s.merchant_category || 'general'}</div>
                  <div className="flex gap-1 mt-1.5">
                    <Link to="/citypro" className="flex-1 px-1.5 py-1 rounded bg-emerald-500 text-black text-[10px] font-black text-center">View</Link>
                    {waUrl && (
                      <a href={waUrl} target="_blank" rel="noreferrer" data-testid={`cp-sc-wa-${s.user_id}`}
                         className="flex-1 px-1.5 py-1 rounded bg-green-600 text-white text-[10px] font-black text-center">📱 WA</a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {!loading && shops.length === 0 && <div className="text-center opacity-60 py-10 text-sm">No shops registered yet. Be the first — register as merchant in CityPro Profile tab!</div>}
      </div>
    </div>
  );
}
